// ============================================================================
// CODE POSTAL ↔ VILLE, D'UN GESTE — l'adresse d'une fiche client (décision utilisateur, comme en caisse).
//
// Cinq chiffres désignent la commune : une seule, elle remplit la ville ; plusieurs, on choisit. Des
// lettres dans la ville proposent « Ville (CP) », qui remplit les deux. La saisie reste LIBRE : ne rien
// trouver n'interdit rien (une adresse à l'étranger, une commune absente du référentiel).
// ⚠️ LA RECHERCHE VIT AU SERVEUR, et c'est la requête de la caisse (`commune-search`, db-schema) : les
//    deux écrans proposent les mêmes communes pour la même saisie.
// ⚠️ UN ÉCHEC SE DIT sous les champs, avec son message réel, et un référentiel ABSENT aussi — « aucune
//    commune » mentirait sur un magasin dont le référentiel n'a pas encore été chargé (§23.0).
// ============================================================================
import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { api, type CommuneOption } from '../api';
import { t } from '../i18n';

/** Les frappes rapprochées se regroupent : une requête par pause, pas une par touche. */
const PAUSE_MS = 180;
/** Un code postal français — seul ce qui lui ressemble déclenche la recherche depuis le champ du code. */
const POSTAL_FR = /^\d{3,5}$/;

export function CommuneField({ postalCode, city, disabled, onChange, labelStyle, inputStyle }: {
  postalCode: string;
  city: string;
  disabled?: boolean;
  onChange: (next: { postalCode: string; city: string }) => void;
  labelStyle: CSSProperties;
  inputStyle: CSSProperties;
}) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [options, setOptions] = useState<CommuneOption[]>([]);
  // ⚠️ L'OPTION DÉSIGNÉE AU CLAVIER : le focus reste dans le champ (on continue de taper), la liste la montre et
  //    `aria-activedescendant` la nomme — sans elle, aucune commune proposée ne se choisissait sans souris.
  const [active, setActive] = useState(-1);
  const listId = `${fieldId}-liste`;
  const optionId = (i: number) => `${fieldId}-option-${i}`;
  const [note, setNote] = useState<string | null>(null);
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ⚠️ SEULE LA DERNIÈRE SAISIE RÉPOND : une réponse lente arrivée après une plus récente afficherait
  //    les communes d'un mot que l'opérateur a déjà effacé.
  const tour = useRef(0);
  useEffect(() => () => { if (minuterie.current) clearTimeout(minuterie.current); }, []);

  const oublier = (): void => {
    tour.current += 1;
    if (minuterie.current) clearTimeout(minuterie.current);
    setOptions([]);
  };
  const choisir = (o: CommuneOption): void => {
    oublier();
    onChange({ postalCode: o.postalCode, city: o.city });
  };
  const chercher = (saisie: string, apres?: (trouvees: CommuneOption[]) => void): void => {
    if (minuterie.current) clearTimeout(minuterie.current);
    const n = ++tour.current;
    minuterie.current = setTimeout(() => {
      api.searchCommunes(saisie).then((r) => {
        if (n !== tour.current) return;
        setNote(r.referentialMissing ? t('Le référentiel des communes n’est pas encore chargé pour ce magasin : saisie libre.') : null);
        setOptions(r.communes);
        // ⚠️ UNE LISTE NEUVE NE GARDE PAS LA DÉSIGNATION DE L'ANCIENNE : le rang d'avant y désignerait une autre commune.
        setActive(-1);
        apres?.(r.communes);
      }).catch((e: unknown) => {
        if (n !== tour.current) return;
        setOptions([]);
        setNote(t('Recherche des communes impossible : {v}', { v: e instanceof Error ? e.message : String(e) }));
      });
    }, PAUSE_MS);
  };

  /** Le clavier de la liste, commun aux deux champs : ↑/↓ désignent, Entrée choisit, Échap ferme. */
  const touche = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') { oublier(); return; }
    if (options.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % options.length); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a <= 0 ? options.length - 1 : a - 1)); return; }
    if (e.key === 'Enter' && active >= 0 && active < options.length) { e.preventDefault(); choisir(options[active]!); }
  };
  /** Ce qu'un champ annonce de la liste qu'il pilote. */
  const combobox = {
    role: 'combobox' as const, 'aria-autocomplete': 'list' as const, 'aria-controls': listId,
    'aria-expanded': options.length > 0,
    'aria-activedescendant': active >= 0 && active < options.length ? optionId(active) : undefined,
  };

  return (
    <span style={{ position: 'relative', display: 'grid', gridTemplateColumns: '104px 1fr', gap: 10 }}>
      <span><span id={`${fieldId}-0`} style={labelStyle}>{t('Code postal')}</span>
        <input aria-labelledby={`${fieldId}-0`}
          value={postalCode} disabled={disabled} inputMode="numeric" autoComplete="off"
          style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
          onChange={(e) => {
            const v = e.target.value;
            onChange({ postalCode: v, city });
            if (!POSTAL_FR.test(v.trim())) { oublier(); return; }
            // Cinq chiffres et une seule commune : la ville se remplit sans un geste de plus.
            chercher(v, (trouvees) => { if (v.trim().length === 5 && trouvees.length === 1) choisir(trouvees[0]!); });
          }}
          {...combobox}
          onKeyDown={touche}
          onBlur={() => setOptions([])}
        />
      </span>
      <span><span id={`${fieldId}-1`} style={labelStyle}>{t('Ville')}</span>
        <input aria-labelledby={`${fieldId}-1`}
          value={city} disabled={disabled} autoComplete="off" style={inputStyle}
          onChange={(e) => { const v = e.target.value; onChange({ postalCode, city: v }); chercher(v); }}
          {...combobox}
          onKeyDown={touche}
          onBlur={() => setOptions([])}
        />
      </span>
      {options.length > 0 && (
        <div
          id={listId} role="listbox" aria-label={t('Communes proposées')}
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4, maxHeight: 240, overflowY: 'auto', background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', boxShadow: '0 12px 30px -10px rgba(0,0,0,.25)' }}
        >
          {options.map((o, i) => (
            // ⚠️ `onMouseDown` ET NON `onClick` : le clic ferait d'abord perdre le focus au champ, qui
            //    fermerait la liste avant que le choix ne soit lu.
            <button
              key={`${o.postalCode}|${o.city}`} id={optionId(i)} type="button" role="option" aria-selected={i === active}
              // ⚠️ HORS DE LA TABULATION : on y arrive par les flèches du champ, qui garde le focus.
              tabIndex={-1}
              onMouseDown={(e) => { e.preventDefault(); choisir(o); }}
              style={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 10, padding: '8px 12px', border: 'none', background: i === active ? 'var(--teal-100)' : 'transparent', cursor: 'pointer', fontSize: '13px', color: 'var(--ink-900)', textAlign: 'left' }}
            >
              <span>{o.city}</span>
              <span style={{ color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{o.postalCode}</span>
            </button>
          ))}
        </div>
      )}
      {note && <span style={{ gridColumn: '1 / -1', fontSize: '11px', color: 'var(--ink-500)' }}>{note}</span>}
    </span>
  );
}
